import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isFunction } from 'lodash/fp';
import { EditorState, Compartment } from '@codemirror/state';
import {
    EditorView,
    keymap,
    placeholder as placeholderExtension,
    lineNumbers
} from '@codemirror/view';
import {
    defaultKeymap,
    indentWithTab,
    history,
    historyKeymap,
    redo,
    redoDepth,
    undo,
    undoDepth
} from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';
import { autocompletion } from '@codemirror/autocomplete';
import { safeSqlFormatter } from 'app/lib/utils';
import { useDeepEffect } from 'app/lib/hooks';
import { Defaults, Theme } from 'app/lib/constants';
import { ThemeProvider } from 'app/components/core/ThemeProvider';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { useErrorTooltip } from 'app/components/core/ErrorTooltip';
import { sprites } from 'app/components/core/Icon';
import { useValueInserter } from './useValueInserter.js';
import { useSnippetInserter } from './useSnippetInserter.js';
import { useValueUpdater } from './useValueUpdater.js';
import { classHighlightStyles } from './HighlightStyles.js';
import * as styles from './CodeEditor.css';

function useRefCallback(callback) {
    const ref = useRef();
    ref.current = callback ?? Defaults.NOOP;
    return ref;
}

function SubmitButton({ onSubmit, onCancel, submitLabel, isSubmitDisabled, isProcessing }) {
    const { t } = useTranslation();

    if (!isFunction(onCancel)) {
        return (
            <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                onClick={onSubmit}
                disabled={isSubmitDisabled || isProcessing}>
                {submitLabel ?? t('components:CodeEditor.run')}
            </Button>
        );
    }

    return (
        <Button
            size={ButtonSize.SMALL}
            variant={ButtonVariant.PRIMARY}
            onClick={isProcessing ? onCancel : onSubmit}
            disabled={isSubmitDisabled}>
            {isProcessing ? t('components:CodeEditor.cancel') : t('components:CodeEditor.run')}
        </Button>
    );
}

const IS_MAC = window.navigator.platform.startsWith('Mac');

export function CodeEditor({
    className,
    grammar = Defaults.NOOP,
    value = '',
    placeholder = '',
    readOnly = false,
    suggestions = null,
    width = '100%',
    height = '136px',
    onChange = Defaults.NOOP,
    onBlur = Defaults.NOOP,
    onSubmit = null,
    onCancel = null,
    onDiscard = null,
    isPlaceholder = false,
    isProcessing = false,
    isSubmitDisabled = false,
    isCancelDisabled = false,
    submitLabel,
    sourcesCallback,
    columnsCallback,
    buttons = Defaults.ARRAY
}) {
    const { t } = useTranslation();
    const submitHandler = useRefCallback(onSubmit);
    const changeHandler = useRefCallback(onChange);
    const blurHandler = useRefCallback(onBlur);

    const parent = useRef();

    const [editable] = useState(() => new Compartment());
    const [mode] = useState(() => new Compartment());
    const [empty] = useState(() => new Compartment());
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [showFormatError, setShowFormatError] = useState(false);

    function runFormatter(editor) {
        const formattedSql = safeSqlFormatter(editor.state.doc.toString());

        if (formattedSql) {
            editor.dispatch({
                changes: [
                    {
                        from: 0,
                        to: editor.state.doc.length,
                        insert: formattedSql
                    }
                ]
            });
        } else {
            setShowFormatError(true);
            setTimeout(() => setShowFormatError(false), 2000);
        }
    }

    const [editor] = useState(() => {
        const state = EditorState.create({
            doc: '',
            extensions: [
                history(),
                keymap.of([
                    indentWithTab,
                    {
                        key: 'Mod-Enter',
                        run(editor) {
                            submitHandler.current(editor.state.doc.toString());
                            return true;
                        },
                        preventDefault: true
                    },
                    {
                        key: 'Mod-Shift-f',
                        run(editor) {
                            runFormatter(editor);

                            return true;
                        }
                    }
                ]),
                keymap.of(historyKeymap),
                keymap.of(defaultKeymap),
                syntaxHighlighting(classHighlightStyles),
                lineNumbers(),
                mode.of(grammar({ sourcesCallback, columnsCallback })),
                autocompletion({
                    activateOnTyping: true,
                    closeOnBlur: false,
                    icons: false,
                    optionClass: (o) => o.type,
                    addToOptions: [
                        {
                            render: (completion) => renderIcon(completion),
                            position: 10
                        }
                    ]
                }),
                EditorView.domEventHandlers({
                    blur: (_, editor) => {
                        blurHandler.current(editor.state.doc.toString());
                    }
                }),
                EditorView.updateListener.of((update) => {
                    setCanUndo(undoDepth(update.state) > 1);
                    setCanRedo(redoDepth(update.state) > 0);

                    if (update.docChanged) {
                        changeHandler.current(update.state.doc.toString());
                    }
                }),
                editable.of(EditorView.editable.of(false)),
                EditorState.languageData.of(() => [
                    { commentTokens: { line: '--', block: { open: '/*', close: '*/' } } }
                ])
            ]
        });

        return new EditorView({ state });
    });

    // Reconfigure the mode if and when `suggestions` changes
    useDeepEffect(() => {
        if (suggestions) {
            editor.dispatch({
                effects: mode.reconfigure(grammar({ columnsCallback }))
            });
        }
    }, [grammar, editor, mode, suggestions]);

    useEffect(() => {
        editor.dispatch({
            effects: empty.reconfigure(placeholderExtension(placeholder))
        });
    }, [editor, empty, placeholder]);

    // Reconfigure the editor if and when `readOnly` changes
    useEffect(() => {
        editor.dispatch({
            effects: editable.reconfigure(EditorView.editable.of(!readOnly))
        });
    }, [editor, editable, readOnly]);

    useValueUpdater(editor, value);
    useValueInserter(editor);
    useSnippetInserter(editor);

    useEffect(() => {
        const container = parent.current;
        const child = editor.dom;
        container.appendChild(child);
        return () => container.removeChild(child);
    }, [editor]);

    useEffect(() => {
        const { dispatch, state } = editor;
        if (isPlaceholder) {
            dispatch(state.update({ selection: { anchor: 0, head: editor.state.doc.length } }));
            editor.focus();
        }
    }, [isPlaceholder, editor]);

    const style = useMemo(() => ({ width, height }), [width, height]);
    const { ErrorTooltip, getTooltipProps, getReferenceProps } = useErrorTooltip();

    const handleSubmit = useCallback(() => {
        submitHandler.current(editor.state.doc.toString());
        editor.setState(editor.state);
        editor.focus();
    }, [submitHandler, editor]);

    const handleUndo = useCallback(() => {
        undo(editor);
    }, [editor]);

    const handleRedo = useCallback(() => {
        redo(editor);
    }, [editor]);

    const handleFormat = useCallback(() => {
        runFormatter(editor);
    }, [editor]);

    return (
        <ThemeProvider theme={Theme.DARK}>
            <div
                data-testid="code-editor"
                className={classnames(styles.base, className)}
                style={style}>
                <div className={styles.actions}>
                    {isFunction(onDiscard) && (
                        <Button
                            size={ButtonSize.SMALL}
                            variant={ButtonVariant.PRIMARY}
                            onClick={onDiscard}
                            disabled={isCancelDisabled || isProcessing}>
                            {t('components:CodeEditor.discardChanges')}
                        </Button>
                    )}
                    {isFunction(onSubmit) && (
                        <ThemeProvider theme={Theme.LIGHT}>
                            <SubmitButton
                                onSubmit={handleSubmit}
                                onCancel={onCancel}
                                submitLabel={submitLabel}
                                isSubmitDisabled={isSubmitDisabled}
                                isProcessing={isProcessing}
                            />
                        </ThemeProvider>
                    )}
                    <hr />
                    <Button
                        size={ButtonSize.MEDIUM}
                        variant={ButtonVariant.PRIMARY}
                        icon="undo"
                        title={t('components:CodeEditor.undo')}
                        onClick={handleUndo}
                        disabled={!canUndo}
                    />
                    <Button
                        size={ButtonSize.MEDIUM}
                        variant={ButtonVariant.PRIMARY}
                        icon="redo"
                        title={t('components:CodeEditor.redo')}
                        onClick={handleRedo}
                        disabled={!canRedo}
                    />
                    <Button
                        size={ButtonSize.MEDIUM}
                        variant={ButtonVariant.PRIMARY}
                        title={t(`components:CodeEditor.${IS_MAC ? 'formatMacTip' : 'formatTip'}`)}
                        onClick={handleFormat}
                        {...getReferenceProps()}>
                        {t('components:CodeEditor.format')}
                    </Button>
                    {buttons}
                </div>
                <div className={styles.editor} ref={parent} />
            </div>
            {showFormatError && (
                <ErrorTooltip
                    warning={t('components:CodeEditor.formatError')}
                    {...getTooltipProps()}
                />
            )}
        </ThemeProvider>
    );
}

CodeEditor.propTypes = {
    grammar: PropTypes.func,

    /**
     * The raw text value. When used with <Field>, this will automatically be set from
     * the associated form state.
     */
    value: PropTypes.string,
    placeholder: PropTypes.string,

    readOnly: PropTypes.bool,

    suggestions: PropTypes.arrayOf(PropTypes.shape({})),

    width: PropTypes.string,
    height: PropTypes.string,

    /**
     * Function to call whenever the value changes. When used with <Field>, this
     * will automatically be configured to update the associated form state.
     */
    onChange: PropTypes.func,

    /**
     * Function to call whenever the editor is blurred. When used with <Field>, this will
     * automatically be configured to update the associated form state.
     */
    onBlur: PropTypes.func,

    onSubmit: PropTypes.func,

    onCancel: PropTypes.func,

    onDiscard: PropTypes.func,

    isPlaceholder: PropTypes.bool,
    isProcessing: PropTypes.bool,
    isSubmitDisabled: PropTypes.bool,
    isCancelDisabled: PropTypes.bool,
    submitLabel: PropTypes.string,

    sourcesCallback: PropTypes.func,
    columnsCallback: PropTypes.func,

    buttons: PropTypes.arrayOf(PropTypes.node)
};

function renderIcon(completion) {
    if (completion.type === 'function') {
        return;
    }
    const sprite = sprites[completion.icon || completion.type];

    if (!sprite) {
        return;
    }

    const { id, viewBox } = sprite;
    const size = 12;
    const xmlns = 'http://www.w3.org/2000/svg';

    const svg = document.createElementNS(xmlns, 'svg');
    svg.classList.add(styles['ac-icon']);
    svg.setAttributeNS(xmlns, 'viewbox', viewBox);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);

    const use = document.createElementNS(xmlns, 'use');
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${id}`);
    svg.appendChild(use);

    return svg;
}
